import { TradeDirection } from "../types/enums";
import { UIView, VizType } from "../types/vizEnums";
import { GroupLevel } from "../types/cpEnums";
import { LocationLevel } from "../graphql/__generated__/graphql";
import { locationMembersInclude } from "../types/metadata";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";
import { capitalizeFirstLetter } from "./locationUtils";
import { getGroupLevelFromGroupMetadatum } from "./locationUtils";

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface IntraRegionTradeParams {
  exporter: string;
  exporterLocationLevel: LocationLevel | undefined;
  foundMetadatumForExporter: {
    members?: (string | number)[];
    parentId?: string | null;
    groupId?: string;
    [key: string]: unknown;
  };
  importer: string;
  importerLocationLevel: LocationLevel | undefined;
  foundMetadatumForImporter: {
    members?: (string | number)[];
    parentId?: string | null;
    groupId?: string;
    [key: string]: unknown;
  };
}

export interface MarketsOverTimeTitleParams {
  isWorldExporter: boolean;
  isWorldImporter: boolean;
  exporter: string;
  importer: string;
  exporterName?: string;
  importerName?: string;
  productNameString?: string;
  startYear?: number | string;
  endYear?: number | string;
  tradeDirection: string;
}

export interface MarketsTreeTitleParams {
  isWorldExporter: boolean;
  isWorldImporter: boolean;
  importer: string;
  importerName?: string;
  exporter: string;
  exporterName?: string;
  productNameString?: string;
  year?: number | string;
  tradeDirection: string;
}

export interface MarketsDefaultTitleParams {
  isWorldExporter: boolean;
  isWorldImporter: boolean;
  exporter: string;
  exporterName?: string;
  importerName?: string;
  productNameString?: string;
  year?: number | string;
  tradeDirection: string;
}

export interface ProductsTitleParams {
  vizType: VizType;
  exporter: string;
  importer: string;
  exporterName?: string;
  importerName?: string;
  startYear?: number | string;
  endYear?: number | string;
  year?: number | string;
  tradeDirection?: string;
}

// ---------------------------------------------------------------------------
// Pure title-generation functions
// ---------------------------------------------------------------------------

export const determineIfHasIntraRegionTrade = ({
  exporter,
  exporterLocationLevel,
  foundMetadatumForExporter,
  importer,
  importerLocationLevel,
  foundMetadatumForImporter,
}: IntraRegionTradeParams) => {
  let hasIntraRegionTradeRelationship = false;

  if (
    importerLocationLevel === LocationLevel.Country ||
    exporterLocationLevel === LocationLevel.Country
  ) {
    if (
      importerLocationLevel === LocationLevel.Country &&
      exporterLocationLevel === LocationLevel.Group
    ) {
      let exporterMemberCountries = foundMetadatumForExporter.members;
      if (locationMembersInclude(exporterMemberCountries, importer)) {
        hasIntraRegionTradeRelationship = true;
      }
    } else if (
      exporterLocationLevel === LocationLevel.Country &&
      importerLocationLevel === LocationLevel.Group
    ) {
      let importerMemberCountries = foundMetadatumForImporter.members;
      if (locationMembersInclude(importerMemberCountries, exporter)) {
        hasIntraRegionTradeRelationship = true;
      }
    }
  } else if (
    exporterLocationLevel === LocationLevel.Group &&
    importerLocationLevel === LocationLevel.Group
  ) {
    let exporterGroupLevel = getGroupLevelFromGroupMetadatum(
      foundMetadatumForExporter,
    );
    let importerGroupLevel = getGroupLevelFromGroupMetadatum(
      foundMetadatumForImporter,
    );

    if (
      exporterGroupLevel === GroupLevel.Subregion &&
      importerGroupLevel === GroupLevel.Region
    ) {
      if (foundMetadatumForExporter.parentId === importer) {
        hasIntraRegionTradeRelationship = true;
      }
    } else if (
      exporterGroupLevel === GroupLevel.Region &&
      importerGroupLevel === GroupLevel.Subregion
    ) {
      if (foundMetadatumForImporter.parentId === exporter) {
        hasIntraRegionTradeRelationship = true;
      }
    } else if (exporter === importer) {
      hasIntraRegionTradeRelationship = true;
    }
  }

  return hasIntraRegionTradeRelationship;
};

export const generateProductSpaceTitle = ({
  exporterName,
  year,
}: {
  exporterName?: string;
  year?: number | string;
}) => {
  if (!exporterName) return "";
  return `${capitalizeFirstLetter(exporterName)} in the Product Space, ${year}`;
};

export const generateFeasibilityTitle = ({
  exporterName,
  year,
}: {
  exporterName?: string;
  year?: number | string;
}) => {
  if (!exporterName) return "";
  return `Growth Opportunities for ${exporterName}, ${year}`;
};

export const generateMarketShareTitle = ({
  exporterName,
  startYear,
  endYear,
}: {
  exporterName?: string;
  startYear?: number | string;
  endYear?: number | string;
}) => {
  if (!exporterName) return "";
  return `${capitalizeFirstLetter(exporterName)}'s global market share, ${startYear} – ${endYear}`;
};

export const generateMarketsOverTimeTitle = ({
  isWorldExporter,
  isWorldImporter,
  exporter: _exporter,
  importer: _importer,
  exporterName,
  importerName,
  productNameString,
  startYear,
  endYear,
  tradeDirection,
}: MarketsOverTimeTitleParams) => {
  // Don't render title if product name is missing
  if (!productNameString) return "";

  if (isWorldExporter && isWorldImporter) {
    // Both are world
    if (tradeDirection === TradeDirection.Exports) {
      return `Who exported ${productNameString}, ${startYear} – ${endYear}?`;
    } else {
      return `Who imported ${productNameString}, ${startYear} – ${endYear}?`;
    }
  } else if (isWorldExporter) {
    if (!importerName) return "";
    return `Who exported ${productNameString} to ${importerName}, ${startYear} – ${endYear}?`;
  } else if (isWorldImporter) {
    if (!exporterName) return "";
    return `Who imported ${productNameString} from ${exporterName}, ${startYear} – ${endYear}?`;
  } else if (exporterName && importerName) {
    // Both are non-World (bilateral): fall back to exporter-focused title
    return `Who imported ${productNameString} from ${exporterName}, ${startYear} – ${endYear}?`;
  }
  return "";
};

export const generateMarketsTreeTitle = ({
  isWorldExporter,
  isWorldImporter,
  importer: _importer,
  importerName,
  exporter: _exporter,
  exporterName,
  productNameString,
  year,
  tradeDirection,
}: MarketsTreeTitleParams) => {
  // Don't render title if product name is missing
  if (!productNameString) return "";

  if (isWorldExporter && isWorldImporter) {
    // Both are world
    if (tradeDirection === TradeDirection.Exports) {
      return `Who exported {{${productNameString}}} in ${year}?`;
    } else {
      return `Who imported {{${productNameString}}} in ${year}?`;
    }
  } else if (isWorldExporter && importerName) {
    return `Where did ${importerName} import {{${productNameString}}} from in ${year}?`;
  } else if (isWorldImporter && exporterName) {
    return `Where did ${exporterName} export {{${productNameString}}} to in ${year}?`;
  } else if (exporterName && importerName) {
    return `Where did ${exporterName} export {{${productNameString}}} to in ${year}?`;
  }
  return "";
};

export const generateMarketsDefaultTitle = ({
  isWorldExporter,
  isWorldImporter,
  exporter: _exporter,
  exporterName,
  importerName,
  productNameString,
  year,
  tradeDirection,
}: MarketsDefaultTitleParams) => {
  // Don't render title if product name is missing
  if (!productNameString) return "";

  if (isWorldExporter && isWorldImporter) {
    // Both are world
    if (tradeDirection === TradeDirection.Exports) {
      return `Who exported ${productNameString} in ${year}?`;
    } else {
      return `Who imported ${productNameString} in ${year}?`;
    }
  } else if (isWorldExporter) {
    if (!isWorldImporter && importerName) {
      return `Where did ${importerName} import ${productNameString} from in ${year}?`;
    } else {
      return `Who exported ${productNameString} in ${year}?`;
    }
  } else if (isWorldImporter) {
    if (exporterName) {
      return `Where did ${exporterName} export ${productNameString} to in ${year}?`;
    } else {
      return `Who imported ${productNameString} in ${year}?`;
    }
  } else if (exporterName) {
    return `Where did ${exporterName} export ${productNameString} to in ${year}?`;
  }
  return "";
};

export const generateProductsTitle = ({
  vizType,
  exporter,
  importer,
  exporterName,
  importerName,
  startYear,
  endYear,
  year,
  tradeDirection,
}: ProductsTitleParams) => {
  const isImports = tradeDirection === TradeDirection.Imports;

  if (vizType === VizType.OverTime && startYear && endYear) {
    if (importer === worldGroupDatum.groupId && exporterName) {
      return `What did ${exporterName} export, ${startYear} – ${endYear}?`;
    } else if (exporter === worldGroupDatum.groupId && importerName) {
      return `What did ${importerName} import, ${startYear} – ${endYear}?`;
    } else if (
      exporter !== worldGroupDatum.groupId &&
      importer !== worldGroupDatum.groupId &&
      exporterName &&
      importerName
    ) {
      return isImports
        ? `What did ${importerName} import from ${exporterName}, ${startYear} – ${endYear}?`
        : `What did ${exporterName} export to ${importerName}, ${startYear} – ${endYear}?`;
    }
  } else {
    if (importer === worldGroupDatum.groupId && exporterName) {
      return `What did ${exporterName} export in ${year}?`;
    } else if (exporter === worldGroupDatum.groupId && importerName) {
      return `What did ${importerName} import in ${year}?`;
    } else if (
      exporter !== worldGroupDatum.groupId &&
      importer !== worldGroupDatum.groupId &&
      exporterName &&
      importerName
    ) {
      return isImports
        ? `What did ${importerName} import from ${exporterName} in ${year}?`
        : `What did ${exporterName} export to ${importerName} in ${year}?`;
    }
  }
  return "";
};

// ---------------------------------------------------------------------------
// Higher-level dispatch function
// ---------------------------------------------------------------------------

export interface GenerateVisualizationTitleParams {
  vizType: VizType;
  view: UIView;
  exporter: string;
  importer: string;
  exporterName?: string;
  importerName?: string;
  productNameString?: string;
  year?: number | string;
  startYear?: number | string;
  endYear?: number | string;
  tradeDirection: string;
  isWorldExporter: boolean;
  isWorldImporter: boolean;
}

export const generateVisualizationTitle = ({
  vizType,
  view,
  exporter,
  importer,
  exporterName,
  importerName,
  productNameString,
  year,
  startYear,
  endYear,
  tradeDirection,
  isWorldExporter,
  isWorldImporter,
}: GenerateVisualizationTitleParams): string => {
  if (vizType === VizType.ProductSpace) {
    return generateProductSpaceTitle({ exporterName, year });
  } else if (vizType === VizType.Feasibility) {
    return generateFeasibilityTitle({ exporterName, year });
  } else if (vizType === VizType.MarketShare) {
    return generateMarketShareTitle({ exporterName, startYear, endYear });
  } else if (view === UIView.Markets) {
    if (vizType === VizType.OverTime && startYear && endYear) {
      return generateMarketsOverTimeTitle({
        isWorldExporter,
        isWorldImporter,
        exporter,
        importer,
        exporterName,
        importerName,
        productNameString,
        startYear,
        endYear,
        tradeDirection,
      });
    } else if (vizType === VizType.Tree) {
      return generateMarketsTreeTitle({
        isWorldExporter,
        isWorldImporter,
        importer,
        importerName,
        exporter,
        exporterName,
        productNameString,
        year,
        tradeDirection,
      });
    } else {
      return generateMarketsDefaultTitle({
        isWorldExporter,
        isWorldImporter,
        exporter,
        exporterName,
        importerName,
        productNameString,
        year,
        tradeDirection,
      });
    }
  } else if (view === UIView.Products) {
    return generateProductsTitle({
      vizType,
      exporter,
      importer,
      exporterName,
      importerName,
      startYear,
      endYear,
      year,
      tradeDirection,
    });
  }
  return "";
};
